import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import Sidebar from "@/components/Sidebar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Menu,
  Laptop,
  Monitor,
  Tablet,
  Smartphone,
  Plus,
  Minus,
  Shield,
  Truck,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Send,
  Package,
  TrendingDown
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Analytics } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  laptop: Laptop,
  desktop: Monitor,
  tablet: Tablet,
  phone: Smartphone
};

export default function ConfigureSolution() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Flow state
  const [step, setStep] = useState(1); // 1: Category, 2: Device, 3: Configure, 4: Services, 5: Review
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [includeMdm, setIncludeMdm] = useState(true);
  const [deliveryLocation, setDeliveryLocation] = useState("delhi_ncr");
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [supportPackages, setSupportPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [tcoTimeframe, setTcoTimeframe] = useState("1yr");

  // Fetch delivery options and support packages on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [deliveryRes, packagesRes] = await Promise.all([
          axios.get(`${API}/delivery-options`, { withCredentials: true }),
          axios.get(`${API}/support-packages`, { withCredentials: true })
        ]);
        setDeliveryOptions(deliveryRes.data.options);
        setSupportPackages(packagesRes.data.packages);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();
    Analytics.pageView('configure_solution');
  }, []);

  // Fetch devices when category is selected
  useEffect(() => {
    if (selectedCategory) {
      fetchDevices(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchDevices = async (category) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/devices?category=${category}`, {
        withCredentials: true
      });
      setDevices(response.data.devices);
    } catch (error) {
      console.error("Error fetching devices:", error);
      toast.error("Failed to load devices");
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    Analytics.categoryViewed(category);
    setStep(2);
  };

  const handleDeviceSelect = (device) => {
    setSelectedDevice(device);
    setIncludeMdm(device.mdm_compatible);
    Analytics.bundleViewed(device.device_id, device.name, device.base_price);
    setStep(3);
  };

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    if (newQty >= 1 && newQty <= 20) {
      setQuantity(newQty);
    }
  };

  const getServicePrice = (pkg) => {
    if (!pkg || !selectedDevice) return 0;
    const hardwareCost = selectedDevice.base_price * quantity;
    // Basic OEM Support = Apple Care+ price for Apple devices, else 4999 INR/yr per device
    if (pkg.package_id === "pkg_basic001") {
      if (selectedDevice.brand === "Apple" && selectedDevice.apple_care_price > 0)
        return selectedDevice.apple_care_price * quantity;
      return 49 * quantity;
    }
    // Premium IT = 12% of hardware cost, but always at least Basic OEM + 400
    if (pkg.package_id === "pkg_premium001") {
      const basicPrice = selectedDevice.brand === "Apple" && selectedDevice.apple_care_price > 0
        ? selectedDevice.apple_care_price * quantity
        : 49 * quantity;
      return Math.max(hardwareCost * 0.12, basicPrice + 400);
    }
    return (pkg.monthly_price || 0) * 12 * quantity;
  };

  const calculateTotals = () => {
    if (!selectedDevice) return { 
      hardwareCost: 0, 
      mdmAnnual: 0, 
      serviceAnnual: 0, 
      firstYearTotal: 0, 
      marketTotal: 0,
      totalSavings: 0 
    };
    
    const hardwareCost = selectedDevice.base_price * quantity;
    const marketHardware = selectedDevice.market_price * quantity;
    
    // MDM/EPM = 15% of hardware cost annually
    const mdmAnnual = includeMdm ? hardwareCost * 0.15 : 0;
    
    // Service package pricing
    let serviceAnnual = 0;
    if (selectedPackage) {
      const pkg = supportPackages.find(p => p.package_id === selectedPackage);
      if (pkg) {
        serviceAnnual = getServicePrice(pkg);
      }
    }
    
    // First year total = hardware + MDM + service
    const firstYearTotal = hardwareCost + mdmAnnual + serviceAnnual;
    
    // Market comparison (hardware only, 20% higher for services)
    const marketMdmAnnual = includeMdm ? marketHardware * 0.18 : 0; // Market MDM is ~18%
    const marketServiceAnnual = serviceAnnual * 1.2; // Market services 20% higher
    const marketTotal = marketHardware + marketMdmAnnual + marketServiceAnnual;
    
    const totalSavings = marketTotal - firstYearTotal;
    
    return { 
      hardwareCost, 
      mdmAnnual,
      serviceAnnual,
      firstYearTotal,
      marketTotal,
      totalSavings
    };
  };

  const getDeliveryDays = () => {
    const option = deliveryOptions.find(o => o.id === deliveryLocation);
    return option?.days || null;
  };

  const calculateTCO = (years = 1) => {
    if (!selectedDevice) return null;
    const hw = selectedDevice.base_price * quantity;
    const mktHw = selectedDevice.market_price * quantity;

    // OneIT costs
    const mdm = includeMdm ? hw * 0.15 * years : 0;
    const svc = (() => {
      if (!selectedPackage) return 0;
      const pkg = supportPackages.find(p => p.package_id === selectedPackage);
      if (!pkg) return 0;
      return getServicePrice(pkg) * years;
    })();
    const impl = hw * 0.18;
    const oneItTotal = hw + mdm + svc + impl;

    // Market costs
    const mktMdm = includeMdm ? mktHw * 0.18 * years : 0;
    const mktSvc = svc * 1.2;
    const mktImpl = mktHw * 0.08;
    const mktTotal = mktHw + mktMdm + mktSvc + mktImpl;

    const savings = mktTotal - oneItTotal;
    const savingsPct = mktTotal > 0 ? Math.round((savings / mktTotal) * 100) : 0;

    return { hw, mktHw, mdm, mktMdm, svc, mktSvc, impl, mktImpl, oneItTotal, mktTotal, savings, savingsPct };
  };

  const handleSubmitRequest = async () => {
    if (!selectedDevice) return;
    
    Analytics.offerRequestStarted(selectedDevice.device_id);
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/offers/request`, {
        device_id: selectedDevice.device_id,
        quantity,
        include_mdm: includeMdm,
        delivery_location: deliveryLocation,
        support_package_id: selectedPackage
      }, { withCredentials: true });
      
      const totals = calculateTotals();
      Analytics.offerRequestCompleted(
        selectedDevice.device_id,
        selectedDevice.name,
        totals.subtotal,
        !!selectedPackage
      );
      
      setRequestId(response.data.request_id);
      setShowThankYou(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const totals = calculateTotals();
  const deliveryDays = getDeliveryDays();

  const categories = [
    { id: "laptop", name: "Laptops", icon: Laptop, description: "Business laptops for productivity" },
    { id: "desktop", name: "Desktops", icon: Monitor, description: "Powerful desktop workstations" },
    { id: "tablet", name: "Tablets", icon: Tablet, description: "Tablets for mobile workforce" },
    { id: "phone", name: "iPhones", icon: Smartphone, description: "Business smartphones" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
      />

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-heading font-semibold text-lg">Configure Your Solution</h1>
                <p className="text-sm text-muted-foreground">
                  Step {step} of 5
                </p>
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="px-4 pb-4">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s <= step ? "bg-[#007AFF]" : "bg-border"
                  }`}
                />
              ))}
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span className={step >= 1 ? "text-foreground font-medium" : ""}>Category</span>
              <span className={step >= 2 ? "text-foreground font-medium" : ""}>Device</span>
              <span className={step >= 3 ? "text-foreground font-medium" : ""}>Configure</span>
              <span className={step >= 4 ? "text-foreground font-medium" : ""}>Services</span>
              <span className={step >= 5 ? "text-foreground font-medium" : ""}>Review</span>
            </div>
          </div>
        </header>

        <main className="p-6 max-w-4xl mx-auto space-y-8">
          
          {/* Step 1: Select Category */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="font-heading font-semibold text-2xl mb-2">What type of device do you need?</h2>
                <p className="text-muted-foreground">Select a category to browse available devices</p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.id)}
                      className="bg-card border border-border rounded-xl p-6 text-center hover:border-[#007AFF] hover:bg-[#007AFF]/5 transition-all group"
                      data-testid={`category-${cat.id}`}
                    >
                      <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4 group-hover:bg-[#007AFF]/10">
                        <Icon className="w-8 h-8 text-[#007AFF]" />
                      </div>
                      <h3 className="font-heading font-semibold text-lg mb-2">{cat.name}</h3>
                      <p className="text-sm text-muted-foreground">{cat.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Select Device */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h2 className="font-heading font-semibold text-2xl">Select a {selectedCategory}</h2>
                  <p className="text-muted-foreground">Choose the device that fits your needs</p>
                </div>
              </div>

              {loading ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="skeleton h-48 rounded-xl" />
                  ))}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {devices.map((device) => (
                    <button
                      key={device.device_id}
                      onClick={() => handleDeviceSelect(device)}
                      className="bg-card border border-border rounded-xl p-5 text-left hover:border-[#007AFF] transition-all"
                      data-testid={`device-${device.device_id}`}
                    >
                      <div className="flex gap-4">
                        <img
                          src={device.image_url}
                          alt={device.name}
                          className="w-24 h-24 object-cover rounded-lg bg-secondary"
                        />
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground mb-1">{device.brand}</p>
                          <h3 className="font-heading font-semibold mb-2">{device.name}</h3>
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-bold font-heading text-[#007AFF]">
                              ₹{device.base_price.toLocaleString()}
                            </span>
                            <span className="text-sm text-muted-foreground line-through">
                              ₹{device.market_price.toLocaleString()}
                            </span>
                          </div>
                          <p className="text-xs text-[#00FF88] mt-1">
                            Save {Math.round((1 - device.base_price / device.market_price) * 100)}%
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Configure (Quantity, MDM, Delivery) */}
          {step === 3 && selectedDevice && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h2 className="font-heading font-semibold text-2xl">Configure Your Order</h2>
                  <p className="text-muted-foreground">{selectedDevice.name}</p>
                </div>
              </div>

              {/* Selected Device Summary */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex gap-4">
                  <img
                    src={selectedDevice.image_url}
                    alt={selectedDevice.name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div>
                    <p className="text-sm text-muted-foreground">{selectedDevice.brand}</p>
                    <h3 className="font-heading font-semibold">{selectedDevice.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{selectedDevice.description}</p>
                  </div>
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="bg-card border border-border rounded-xl p-5">
                <h3 className="font-heading font-semibold mb-4">Quantity</h3>
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground">How many units do you need? (Max 20)</p>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                      data-testid="qty-minus"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-2xl font-heading font-bold w-12 text-center">{quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= 20}
                      data-testid="qty-plus"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* MDM/EPM Toggle */}
              {selectedDevice.mdm_compatible && (
                <div className="bg-card border border-border rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-[#007AFF]" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold">
                          {["tablet","phone"].includes(selectedCategory) ? "MDM" : "EPM"} - Device Management
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {selectedCategory === "tablet" 
                            ? "Mobile Device Management for secure tablet deployment"
                            : "Endpoint Management for security and compliance"
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-[#007AFF] font-heading">
                        ₹{selectedDevice.mdm_monthly_price}/device/mo
                      </span>
                      <Switch
                        checked={includeMdm}
                        onCheckedChange={setIncludeMdm}
                        data-testid="mdm-toggle"
                      />
                    </div>
                  </div>
                  {includeMdm && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-sm text-[#00FF88]">
                        ✓ Total: ₹{(selectedDevice.mdm_monthly_price * quantity).toLocaleString()}/month for {quantity} device{quantity > 1 ? 's' : ''}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Delivery Location */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#007AFF]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold">Delivery Location</h3>
                    <p className="text-sm text-muted-foreground">Select your delivery region</p>
                  </div>
                </div>
                
                <Select value={deliveryLocation} onValueChange={setDeliveryLocation}>
                  <SelectTrigger data-testid="delivery-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {deliveryOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name} {option.days ? `(${option.days} days)` : `- ${option.message}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {deliveryDays && (
                  <p className="text-sm text-[#00FF88] mt-3">
                    ✓ Estimated delivery: {deliveryDays} business days
                  </p>
                )}
                {deliveryLocation === "custom" && (
                  <p className="text-sm text-yellow-500 mt-3">
                    Custom configurations take 45-60 days. Our team will coordinate delivery across India.
                  </p>
                )}
              </div>

              {/* TCO Preview - Contextual for selected device */}
              <div className="bg-gradient-to-r from-[#00FF88]/10 to-[#00FF88]/5 border border-[#00FF88]/20 rounded-xl p-5">
                <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                  <span className="text-[#00FF88]">💰</span> Cost Summary (First Year)
                </h3>
                
                {/* Cost Breakdown */}
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Hardware ({quantity}x {selectedDevice.name})</span>
                    <span className="font-heading font-semibold">₹{totals.hardwareCost.toLocaleString()}</span>
                  </div>
                  {includeMdm && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{["tablet","phone"].includes(selectedCategory) ? "MDM" : "EPM"} - Device Management</span>
                      <span className="font-heading font-semibold">₹{Math.round(totals.mdmAnnual).toLocaleString()}/year</span>
                    </div>
                  )}
                  {!includeMdm && (
                    <div className="flex justify-between items-center text-muted-foreground/50">
                      <span>{["tablet","phone"].includes(selectedCategory) ? "MDM" : "EPM"} (not selected)</span>
                      <span>—</span>
                    </div>
                  )}
                  <div className="border-t border-[#00FF88]/20 pt-3 flex justify-between items-center">
                    <span className="font-heading font-semibold">Subtotal (before services)</span>
                    <span className="text-xl font-heading font-bold text-[#007AFF]">
                      ₹{Math.round(totals.hardwareCost + totals.mdmAnnual).toLocaleString()}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">
                  Select support services in the next step to see your complete first-year total.
                </p>
                <p className="text-xs text-muted-foreground/60 mt-2 border-t border-[#00FF88]/10 pt-2">
                  All prices are exclusive of taxes / GST.
                </p>
              </div>

              <Button
                onClick={() => setStep(4)}
                className="w-full bg-[#007AFF] hover:bg-[#0055CC] h-12"
                data-testid="continue-to-services"
              >
                Continue to Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 4: Support Services */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => setStep(3)}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h2 className="font-heading font-semibold text-2xl">Add Support Services</h2>
                  <p className="text-muted-foreground">Optional warranty and support packages</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* No Package Option */}
                <button
                  onClick={() => setSelectedPackage(null)}
                  className={`w-full bg-card border rounded-xl p-5 text-left transition-all ${
                    selectedPackage === null
                      ? "border-[#007AFF] bg-[#007AFF]/5"
                      : "border-border hover:border-[#007AFF]/20"
                  }`}
                  data-testid="no-package"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading font-semibold">No Additional Support</h3>
                      <p className="text-sm text-muted-foreground">Standard manufacturer warranty only</p>
                    </div>
                    {selectedPackage === null && (
                      <CheckCircle2 className="w-6 h-6 text-[#007AFF]" />
                    )}
                  </div>
                </button>

                {supportPackages.map((pkg) => {
                  const servicePrice = getServicePrice(pkg);
                  return (
                    <button
                      key={pkg.package_id}
                      onClick={() => setSelectedPackage(pkg.package_id)}
                      className={`w-full bg-card border rounded-xl p-5 text-left transition-all relative ${
                        selectedPackage === pkg.package_id
                          ? "border-[#007AFF] bg-[#007AFF]/5"
                          : "border-border hover:border-[#007AFF]/20"
                      }`}
                      data-testid={`package-${pkg.package_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-heading font-semibold">{pkg.name}</h3>
                            {pkg.is_recommended && (
                              <span className="px-2 py-0.5 bg-[#00FF88]/10 text-[#00FF88] text-xs rounded-full">
                                Recommended
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {pkg.package_id === "pkg_basic001" && selectedDevice?.brand === "Apple" && selectedDevice?.apple_care_price > 0
                              ? "Includes Apple Care+ — OEM warranty & accidental damage by Apple"
                              : pkg.description
                            }
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-xl font-bold font-heading text-[#007AFF]">
                            ₹{Math.round(servicePrice).toLocaleString()}
                          </span>
                          <span className="text-muted-foreground">/year</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {pkg.features.slice(0, 4).map((feature, i) => (
                          <span key={i} className="text-xs bg-secondary px-2 py-1 rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                      {selectedPackage === pkg.package_id && (
                        <div className="absolute top-3 right-3">
                          <CheckCircle2 className="w-6 h-6 text-[#007AFF]" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <p className="text-xs text-muted-foreground/60 text-center">All prices are exclusive of taxes / GST.</p>

              <Button
                onClick={() => setStep(5)}
                className="w-full bg-[#007AFF] hover:bg-[#0055CC] h-12"
                data-testid="continue-to-review"
              >
                Review Order
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 5: Review & Submit */}
          {step === 5 && selectedDevice && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => setStep(4)}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h2 className="font-heading font-semibold text-2xl">Review Your Order</h2>
                  <p className="text-muted-foreground">Confirm details and request your quote</p>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-5 border-b border-border">
                  <h3 className="font-heading font-semibold">Order Summary</h3>
                </div>
                
                {/* Device */}
                <div className="p-5 border-b border-border">
                  <div className="flex gap-4">
                    <img
                      src={selectedDevice.image_url}
                      alt={selectedDevice.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground">{selectedDevice.brand}</p>
                      <h4 className="font-heading font-semibold">{selectedDevice.name}</h4>
                      <p className="text-sm text-muted-foreground">Qty: {quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading font-bold">₹{totals.hardwareCost.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* MDM/EPM */}
                {includeMdm && (
                  <div className="p-5 border-b border-border flex justify-between">
                    <div>
                      <h4 className="font-heading font-semibold">
                        {["tablet","phone"].includes(selectedCategory) ? "MDM" : "EPM"} - Device Management
                      </h4>
                      <p className="text-sm text-muted-foreground">{quantity} device{quantity > 1 ? 's' : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading">₹{Math.round(totals.mdmAnnual).toLocaleString()}/year</p>
                    </div>
                  </div>
                )}

                {/* Support Package */}
                {selectedPackage && (
                  <div className="p-5 border-b border-border flex justify-between">
                    <div>
                      <h4 className="font-heading font-semibold">
                        {supportPackages.find(p => p.package_id === selectedPackage)?.name}
                      </h4>
                      <p className="text-sm text-muted-foreground">Annual support plan</p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading">
                        ₹{Math.round(totals.serviceAnnual).toLocaleString()}/year
                      </p>
                    </div>
                  </div>
                )}

                {/* Delivery */}
                <div className="p-5 border-b border-border flex justify-between">
                  <div>
                    <h4 className="font-heading font-semibold">Delivery</h4>
                    <p className="text-sm text-muted-foreground">
                      {deliveryOptions.find(o => o.id === deliveryLocation)?.name}
                    </p>
                  </div>
                  <div className="text-right">
                    {deliveryDays ? (
                      <p className="text-[#00FF88]">{deliveryDays} business days</p>
                    ) : (
                      <p className="text-yellow-500">Contact Sales</p>
                    )}
                  </div>
                </div>

                {/* Total */}
                <div className="p-5 bg-secondary/30">
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Hardware</span>
                      <span>₹{totals.hardwareCost.toLocaleString()}</span>
                    </div>
                    {includeMdm && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{["tablet","phone"].includes(selectedCategory) ? "MDM" : "EPM"} (Annual)</span>
                        <span>₹{Math.round(totals.mdmAnnual).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedPackage && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Support (Annual)</span>
                        <span>₹{Math.round(totals.serviceAnnual).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="border-t border-border pt-3 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground text-sm">Subtotal (excl. GST)</span>
                        <span className="font-heading font-semibold">
                          ₹{Math.round(totals.firstYearTotal).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground text-sm">GST (18%)</span>
                        <span className="font-heading font-semibold">
                          ₹{Math.round(totals.firstYearTotal * 0.18).toLocaleString()}
                        </span>
                      </div>
                      <div className="border-t border-border pt-2 flex justify-between items-center">
                        <div>
                          <h4 className="font-heading font-semibold text-lg">Total (incl. GST)</h4>
                          <p className="text-xs text-muted-foreground">GST charged at 18% of total invoice value</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-heading font-bold text-[#007AFF]">
                            ₹{Math.round(totals.firstYearTotal * 1.18).toLocaleString()}
                          </p>
                          {totals.totalSavings > 0 && (
                            <p className="text-sm text-[#00FF88]">
                              Save ₹{Math.round(totals.totalSavings).toLocaleString()} vs market
                            </p>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground/60 pt-1">All prices are exclusive of taxes / GST unless stated.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* TCO Analysis */}
              {(() => {
                const years = tcoTimeframe === "1yr" ? 1 : 3;
                const tco = calculateTCO(years);
                if (!tco) return null;
                return (
                  <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="tco-section">
                    <div className="p-5 border-b border-border flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-5 h-5 text-[#00FF88]" />
                        <h3 className="font-heading font-semibold">TCO vs Market Comparison</h3>
                      </div>
                      {/* Timeframe toggle */}
                      <div className="flex rounded-lg border border-border overflow-hidden text-sm">
                        <button
                          onClick={() => setTcoTimeframe("1yr")}
                          className={`px-4 py-1.5 font-medium transition-colors ${tcoTimeframe === "1yr" ? "bg-[#007AFF] text-white" : "text-muted-foreground hover:text-foreground"}`}
                          data-testid="tco-toggle-1yr"
                        >
                          1 Year
                        </button>
                        <button
                          onClick={() => setTcoTimeframe("3yr")}
                          className={`px-4 py-1.5 font-medium transition-colors ${tcoTimeframe === "3yr" ? "bg-[#007AFF] text-white" : "text-muted-foreground hover:text-foreground"}`}
                          data-testid="tco-toggle-3yr"
                        >
                          3 Years
                        </button>
                      </div>
                    </div>

                    {/* Breakdown table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border bg-secondary/30">
                            <th className="p-4 text-left text-muted-foreground font-medium">Category</th>
                            <th className="p-4 text-right font-medium text-[#007AFF]">OneIT Price</th>
                            <th className="p-4 text-right font-medium text-muted-foreground">Market Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-border/50">
                            <td className="p-4">Hardware ({quantity}x {selectedDevice.name})</td>
                            <td className="p-4 text-right font-mono">₹{Math.round(tco.hw).toLocaleString()}</td>
                            <td className="p-4 text-right font-mono text-muted-foreground">₹{Math.round(tco.mktHw).toLocaleString()}</td>
                          </tr>
                          {includeMdm && (
                            <tr className="border-b border-border/50">
                              <td className="p-4">{["tablet","phone"].includes(selectedCategory) ? "MDM" : "EPM"} ({years} yr{years > 1 ? "s" : ""})</td>
                              <td className="p-4 text-right font-mono">₹{Math.round(tco.mdm).toLocaleString()}</td>
                              <td className="p-4 text-right font-mono text-muted-foreground">₹{Math.round(tco.mktMdm).toLocaleString()}</td>
                            </tr>
                          )}
                          {selectedPackage && (
                            <tr className="border-b border-border/50">
                              <td className="p-4">Support Services ({years} yr{years > 1 ? "s" : ""})</td>
                              <td className="p-4 text-right font-mono">₹{Math.round(tco.svc).toLocaleString()}</td>
                              <td className="p-4 text-right font-mono text-muted-foreground">₹{Math.round(tco.mktSvc).toLocaleString()}</td>
                            </tr>
                          )}
                          <tr className="border-b border-border/50">
                            <td className="p-4">Implementation</td>
                            <td className="p-4 text-right font-mono">₹{Math.round(tco.impl).toLocaleString()}</td>
                            <td className="p-4 text-right font-mono text-muted-foreground">₹{Math.round(tco.mktImpl).toLocaleString()}</td>
                          </tr>
                          <tr className="bg-secondary/30 font-semibold">
                            <td className="p-4">{years}-Year Total</td>
                            <td className="p-4 text-right font-mono text-[#007AFF]">₹{Math.round(tco.oneItTotal).toLocaleString()}</td>
                            <td className="p-4 text-right font-mono text-muted-foreground">₹{Math.round(tco.mktTotal).toLocaleString()}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Savings callout */}
                    <div className="p-5 bg-[#00FF88]/5 border-t border-[#00FF88]/20 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-5 h-5 text-[#00FF88]" />
                        <span className="font-heading font-semibold text-[#00FF88]">
                          You save ₹{Math.round(tco.savings).toLocaleString()} ({tco.savingsPct}%) vs market
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">over {years} year{years > 1 ? "s" : ""}</span>
                    </div>
                  </div>
                );
              })()}

              <Button
                onClick={handleSubmitRequest}
                disabled={submitting}
                className="w-full bg-[#00FF88] text-black hover:bg-[#00CC6A] h-14 text-lg font-semibold"
                data-testid="submit-request"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Request Quote
                  </>
                )}
              </Button>
            </div>
          )}
        </main>
      </div>

      {/* Thank You Dialog */}
      <Dialog open={showThankYou} onOpenChange={setShowThankYou}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <div className="w-16 h-16 rounded-full bg-[#00FF88]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#00FF88]" />
            </div>
            <DialogTitle className="text-center text-xl">Quote Request Submitted!</DialogTitle>
            <DialogDescription className="text-center">
              Thank you for your request. Our team will review your configuration and get back to you within 12 hours.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-secondary/50 rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground">Request ID</p>
            <p className="font-mono font-semibold">{requestId}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setShowThankYou(false);
                // Reset flow
                setStep(1);
                setSelectedCategory(null);
                setSelectedDevice(null);
                setQuantity(1);
                setIncludeMdm(true);
                setSelectedPackage(null);
              }}
            >
              Configure Another
            </Button>
            <Button
              className="flex-1 bg-[#007AFF] hover:bg-[#0055CC]"
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
